import { api } from "@/lib/trpc/client";
import { DeleteHandler, NodeApi, RenameHandler, SimpleTree, TreeApi } from "react-arborist";
import { Data } from "./types";
import { useEffect, useMemo, useRef, useState } from "react";

export const useTree = (
  officialMonitorGroups: Data[],
  refetchGroups: () => void,
  institutionId: string,
) => {
  const addEditGroupMutation = api.officialMonitor.adminRouter.addEditGroup.useMutation();
  const deleteGroupMutation = api.officialMonitor.adminRouter.deleteGroup.useMutation();

  const [data, setData] = useState<Data[]>(officialMonitorGroups);

  const tree = useMemo(() => new SimpleTree<Data>(data), [data]);
  const treeRef = useRef<TreeApi<Data> | null>(null);

  useEffect(() => {
    setData(officialMonitorGroups);
  }, [officialMonitorGroups]);

  const handleAddRegistratureInsideNode = (node: NodeApi<Data>, registratureId: string) => {
    addEditGroupMutation.mutate(
      {
        id: node.data.id,
        registratureId,
        institutionId,
      },
      {
        onSuccess: refetchGroups,
      },
    );
  };

  const handleAddGroupInsideNode = (node: NodeApi<Data>) => {
    const groupName = "New group";

    addEditGroupMutation.mutate(
      {
        parentId: node.data.id,
        displayName: groupName,
        institutionId,
      },
      {
        onSuccess: refetchGroups,
      },
    );

    setData(tree.data);
  };

  const onRename: RenameHandler<Data> = async ({ name, id }) => {
    tree.update({ id, changes: { name } as Partial<Data> });

    const node = tree.find(id);

    if (!node) return;

    setData(tree.data);
    const parentId = node.parent?.data.id === "ROOT" ? undefined : node?.parent?.data.id;
    addEditGroupMutation.mutate({
      id,
      displayName: name,
      parentId,
      institutionId,
    });
  };

  const onCreateRootGroup = () => {
    addEditGroupMutation.mutate(
      {
        displayName: "New group",
        parentId: null,
        institutionId,
      },
      {
        onSuccess: (response) => {
          tree.create({
            parentId: "ROOT",
            index: 0,
            data: {
              id: response!.id,
              name: "New group",
              type: "group",
              children: [],
            },
          });
          setData(tree.data);
          setTimeout(() => {
            const insertedNode = treeRef.current?.get(response!.id);
            if (!insertedNode) return;
            insertedNode.edit();
          }, 100);
        },
      },
    );

    return null;
  };

  const onDelete: DeleteHandler<Data> = (args: { ids: string[] }) => {
    args.ids.forEach((id) => tree.drop({ id }));

    setData(tree.data);
  };

  const onDeleteFolder = (node: NodeApi<Data>) => {
    deleteGroupMutation.mutate({ id: node.data.id }, { onSuccess: refetchGroups });
  };

  return {
    handleAddGroupInsideNode,
    handleAddRegistratureInsideNode,
    tree,
    treeRef,
    onRename,
    onCreateRootGroup,
    onDelete,
    onDeleteFolder,
    data,
  };
};
